import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export interface ContentBoxProps {
  title: string;
  description?: string | ReactNode;
  index: number;
  selected?: boolean;
  completed?: boolean;
  backgroundColor?: string;
  isMobile?: boolean;
  showConnector?: boolean;
  connectorCompleted?: boolean;
}

const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path
      d='M5 13l4 4L19 7'
      stroke='currentColor'
      strokeWidth='2.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const StepBadge = ({
  index,
  selected,
  completed,
  backgroundColor,
  isMobile,
}: Pick<
  ContentBoxProps,
  'index' | 'selected' | 'completed' | 'backgroundColor' | 'isMobile'
>) => {
  const badgeSize = isMobile ? 'h-8 w-8 text-sm' : 'h-[42px] w-[42px] text-lg';
  const isActive = selected && !completed;
  const isRed = backgroundColor?.includes('red');
  const pulseColor = isRed ? 'bg-redSecondary/30' : 'bg-greenSecondary/30';
  const pulseRingSize = isMobile ? 'h-9 w-9' : 'h-12 w-12';
  const pulseRingSizeOuter = isMobile ? 'h-10 w-10' : 'h-[52px] w-[52px]';

  return (
    <div className='relative flex items-center justify-center'>
      {isActive ? (
        <>
          <span
            className={twMerge(
              'timeline-step-pulse absolute rounded-full',
              pulseColor,
              pulseRingSize
            )}
            aria-hidden='true'
          />
          <span
            className={twMerge(
              'timeline-step-pulse timeline-step-pulse-delay absolute rounded-full',
              pulseColor,
              pulseRingSizeOuter
            )}
            aria-hidden='true'
          />
        </>
      ) : null}
      <div
        className={twMerge(
          `relative z-10 flex ${badgeSize} shrink-0 items-center justify-center rounded-full font-bold transition-all duration-200`,
          completed
            ? 'bg-greenSecondary text-white shadow-sm ring-2 ring-white'
            : selected
            ? twMerge(
                `${backgroundColor} text-white shadow-md ${
                  isMobile ? 'ring-2' : 'ring-[3px]'
                } ring-white`,
                isRed ? 'timeline-step-glow-red' : 'timeline-step-glow-green'
              )
            : 'border border-gray-200/80 bg-white text-gray-400'
        )}
      >
        {completed ? (
          <CheckIcon className={isMobile ? 'h-4 w-4' : 'h-5 w-5'} />
        ) : (
          index + 1
        )}
      </div>
    </div>
  );
};

const TimelineConnector = ({
  completed,
  isMobile,
}: {
  completed?: boolean;
  isMobile?: boolean;
}) => (
  <div
    className={twMerge(
      'flex flex-col items-center',
      isMobile ? 'h-12 py-1' : 'h-14 py-1.5'
    )}
    aria-hidden='true'
  >
    <div
      className={twMerge(
        'w-[2px] flex-1 rounded-full',
        completed
          ? 'bg-gradient-to-b from-greenSecondary via-greenSecondary/70 to-greenSecondary/30'
          : 'bg-gradient-to-b from-gray-200 via-gray-200/80 to-gray-100'
      )}
    />
    <div
      className={twMerge(
        'mt-1 h-1.5 w-1.5 shrink-0 rounded-full ring-2 ring-[#f5f7f8]',
        completed ? 'bg-greenSecondary' : 'bg-gray-300'
      )}
    />
  </div>
);

export const TimelineStep = ({
  title,
  description,
  index,
  selected,
  completed,
  backgroundColor,
  isMobile,
  showConnector,
  connectorCompleted,
}: ContentBoxProps) => {
  const railWidth = isMobile ? 'w-8' : 'w-[42px]';

  const hasDescription =
    description !== undefined && description !== null && description !== '';

  const isRed = backgroundColor?.includes('red');

  const titleClassName = twMerge(
    'font-headings font-semibold leading-snug',
    isMobile ? 'text-sm' : 'text-[20px]',
    selected ? 'text-gray-900' : 'text-graySecondary'
  );

  const descriptionClassName = twMerge(
    'leading-relaxed text-grayTertiary',
    isMobile ? 'mt-0.5 text-xs' : 'mt-1 text-[17px]',
    selected && 'text-graySecondary'
  );

  const contentClassName = twMerge(
    'min-w-0 flex-1',
    selected && (isRed ? 'timeline-step-active-red' : 'timeline-step-active')
  );

  const content = (
    <div className={contentClassName}>
      <div className={titleClassName}>{title}</div>
      {hasDescription ? (
        <div className={descriptionClassName}>{description}</div>
      ) : null}
    </div>
  );

  return (
    <div className={twMerge('w-full', isMobile ? 'px-2' : 'px-3')}>
      <div className='flex items-center gap-3'>
        <div className={twMerge('flex shrink-0 justify-center', railWidth)}>
          <StepBadge
            backgroundColor={backgroundColor}
            completed={completed}
            index={index}
            isMobile={isMobile}
            selected={selected}
          />
        </div>
        {content}
      </div>

      {showConnector ? (
        <div className='flex'>
          <div className={twMerge('flex shrink-0 justify-center', railWidth)}>
            <TimelineConnector
              completed={connectorCompleted}
              isMobile={isMobile}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

/** @deprecated Use TimelineStep instead */
export const ContentBox = (props: ContentBoxProps) => (
  <TimelineStep {...props} />
);

export type TimelineStepProps = ContentBoxProps;
